import ApiService from '~/utils/ApiService';
import * as MODEL from '~/constants/ModelConstants';
import _ from 'lodash';
import CvssService from '~/utils/CvssService';
import Helpers from '~/utils/Helpers';

export const SELECT_SEARCH_DATA_FILTERS = 'SELECT_SEARCH_DATA_FILTERS';
export const REQUEST_SEARCH_DATA_RESULT = 'REQUEST_SEARCH_DATA_RESULT';
export const RECEIVE_SEARCH_DATA_RESULT = 'RECEIVE_SEARCH_DATA_RESULT';
export const SET_SEARCH_DATA_FILTER = 'SET_SEARCH_DATA_FILTER';
export const REQUEST_SEARCH_DATA_FILTER = 'REQUEST_SEARCH_DATA_FILTER';
export const SEARCH_DATA_BE_ERROR = 'SEARCH_DATA_BE_ERROR';
export const UPDATE_SEVERITY_VAL = 'UPDATE_SEVERITY_VAL';
export const UPDATE_DATE_OPT = 'UPDATE_DATE_OPT';
export const UPDATE_IS_SEARCH_MODE = 'UPDATE_IS_SEARCH_MODE';
export const UPDATE_CVSS_SELECTION = 'UPDATE_CVSS_SELECTION';

export const normalizeQuery = query => {
  // in the second replace() method, we included slash ("/") in the regex because Go libraries are usually named domain/owner/lib
  return _.clone(query)
    .trim()
    .toLowerCase()
    .replace(/\\/g, '')
    .replace(/[^A-Za-z0-9_\s-:.,/<>]/g, '') // List of whitelisted characters
    .replace(/CVE-/i, '');
};

export const extractFacet = (query, facetName, defaultValue) => {
  const result = query.match(new RegExp(facetName + ':([^\\s]+)', 'i'));
  if (result && result.length > 1) {
    return MODEL.searchSingleValueFacets.indexOf(facetName) >= 0 ? result[1] : result[1].split(',');
  }

  return defaultValue;
};

export const removeFacet = (query, facetName) => {
  const result = query.replace(new RegExp(facetName + ':[^\\s]+', 'gi'), ''); // remove this facet if already in
  return result.replace(/\s+/g, ' ');
};

export const removeFacetValue = (query, facetName, facetValue) => {
  if (query.indexOf(facetName + ':') !== -1) {
    const curRE = new RegExp(facetName + ':([^\\s]+)', 'gi');

    const matches = curRE.exec(query);

    if (matches && matches.length === 2) {
      let existing = matches[1].split(',');
      existing = existing.filter(val => val !== facetValue);
      if (existing.length === 0) {
        return removeFacet(query, facetName);
      }
      const toAdd = facetName + ':' + existing.join(',');
      query = query.replace(new RegExp(facetName + ':[^\\s]+', 'gi'), toAdd);
    }
  }

  return query;
};

export const replaceFacetValue = (query, facetName, facetValue) => {
  const toAdd = facetName + ':' + facetValue;

  // replace in line if already there;
  if (query.indexOf(facetName + ':') !== -1) {
    query = query.replace(new RegExp(facetName + ':[^\\s]+', 'gi'), toAdd);
  }

  // add to end if it's not there still
  if (query.indexOf(toAdd) === -1) {
    query += (query.length > 0 ? ' ' : '') + toAdd;
  }

  return query;
};

export const addFacet = (query = '', facetName, facetValue) => {
  let toAdd = facetName + ':' + facetValue;

  // replace in line if already there;
  if (query.indexOf(facetName + ':') !== -1) {
    const curRE = new RegExp(facetName + ':([^\\s]+)', 'gi');

    const matches = curRE.exec(query);

    if (matches && matches.length === 2) {
      const existing = matches[1].split(',');
      if (existing.indexOf(facetValue) === -1) {
        existing.push(facetValue);
      }
      toAdd = facetName + ':' + existing.join(',');
    }

    query = query.replace(new RegExp(facetName + ':[^\\s]+', 'gi'), toAdd);
  }

  // add to end if it's not there still
  if (query.indexOf(toAdd) === -1) {
    query += (query.length > 0 ? ' ' : '') + toAdd;
  }

  return query;
};

//
// Actions
//

export const setBackendError = () => {
  return {
    type: SEARCH_DATA_BE_ERROR,
    isBackendDown: true,
  };
};

const makeQueryApiCompatible = query => {
  // Place a series of transformations to API compatible values

  let compatibleQuery = Helpers.translateQueryValueToApiCompatibleValue({
    query,
    targetField: 'source',
    valueToTransform: 'native',
    compatibleValuesMap: MODEL.API_COMPATIBLE_QUERY_VALUES_MAP,
  });

  return compatibleQuery;
};

export const search = (query, pageNum, concat) => {
  return function(dispatch, getState) {
    const { myState } = getState();
    let isVeracodeUser = false;

    if (myState && myState.me && myState.me.roles) {
      const { roles } = myState.me;
      isVeracodeUser = _.includes(roles, 'VERACODE_USER');
    }

    dispatch(requestSearchResults());

    query = normalizeQuery(query);
    query = makeQueryApiCompatible(query);
    const qs = '?q=' + encodeURIComponent(query);

    let endpoint = '/catalog/search' + qs;

    if (concat) {
      endpoint += '&page=' + pageNum;
    }

    ApiService.get(endpoint, { shouldUseVeracodeApi: isVeracodeUser }).then(
      results => {
        dispatch(receiveSearchDataResults(results, concat));
      },
      () => {
        dispatch(setBackendError());
      }
    );
  };
};

export const requestSearchResults = () => {
  return {
    type: REQUEST_SEARCH_DATA_RESULT,
    isRequestingResults: true,
  };
};

export const receiveSearchResults = data => {
  return {
    type: RECEIVE_SEARCH_DATA_RESULT,
    isRequestingResults: false,
    searchResults: data.results,
    searchResultCount: data.count,
    isBackendDown: false,
    ranFirstQuery: true,
  };
};

export const receiveSearchDataResults = (data, concat) => {
  return {
    type: RECEIVE_SEARCH_DATA_RESULT,
    isRequestingResults: false,
    contents: data.contents,
    metadata: data.metadata,
    ranFirstQuery: true,
    isFiltering: false,
    isBackendDown: false,
    concat,
  };
};

export const selectSearchFilters = filters => {
  return {
    type: SELECT_SEARCH_DATA_FILTERS,
    searchFilters: filters,
  };
};

export const setNewFilter = newFilters => {
  return {
    type: SET_SEARCH_DATA_FILTER,
    newFilters,
    meta: {
      snowplow: true,
      string1: newFilters.query || '',
    },
  };
};

export const requestSearchDataFilter = () => {
  return {
    type: REQUEST_SEARCH_DATA_FILTER,
    isFiltering: true,
  };
};

export const setFilter = (k, v) => {
  return function(dispatch) {
    const newFilter = {
      [k]: v,
    };
    dispatch(requestSearchDataFilter());
    dispatch(updateFilters(newFilter));
  };
};

export const updateFilters = filters => {
  return function(dispatch, getState) {
    const state = getState().searchDataState;
    const curFilters = Object.assign({}, state.filters);
    const filterKey = Object.keys(filters)[0];
    const filterVal = filters[filterKey];
    const isFilterKeySingleValue = _.includes(MODEL.searchSingleValueFacets, filterKey);

    // Special handling required as user could come in with either of the 3 filter key
    if (['severity', 'cvss2_severity', 'cvss3_severity'].includes(filterKey)) {
      if (filterVal === null) {
        delete filters.severity;
        filters.query = removeFacet(curFilters.query, 'cvss2_severity');
        filters.query = removeFacet(filters.query, 'cvss3_severity');
        filters.query = removeFacet(filters.query, 'severity');
      } else {
        filters.severity = filterVal;

        // For severity case, remove all previous severity and set the current
        filters.query = removeFacet(curFilters.query, 'cvss2_severity');
        filters.query = removeFacet(filters.query, 'cvss3_severity');
        filters.query = removeFacet(filters.query, 'severity');

        // Remove extra spacing at the end
        // Happen during edge case when query is "type:vulnerability severity:2..1"
        filters.query = filters.query.trim();

        filters.query = addFacet(filters.query, filterKey, filterVal);
      }
    } else if (filterKey !== 'query' && filterKey !== 'type') {
      if (!curFilters[filterKey] && filterVal !== null) {
        // this filter is not currently used at all.

        filters[filterKey] = isFilterKeySingleValue ? filterVal : [filterVal];
        filters.query = addFacet(curFilters.query, filterKey, filterVal);
      } else if (isFilterKeySingleValue) {
        //Filter already has a (single, non-array) value
        if (filterVal === null) {
          delete filters[filterKey];
          filters.query = removeFacet(curFilters.query, filterKey);
        } else {
          filters[filterKey] = filterVal;
          filters.query = replaceFacetValue(curFilters.query, filterKey, filterVal);
        }
      } else {
        // filter already has values
        filters[filterKey] = curFilters[filterKey] === 'all' ? [] : curFilters[filterKey].slice(0);

        // the value isn't used for this filter yet.
        if (filters[filterKey].indexOf(filterVal) === -1) {
          filters[filterKey].push(filterVal);
          filters.query = addFacet(curFilters.query, filterKey, filterVal);
        } else {
          filters[filterKey] = filters[filterKey].filter(val => filterVal !== val);
          filters.query = removeFacetValue(curFilters.query, filterKey, filterVal);
        }
      }
    } else if (filterKey === 'type') {
      if (!curFilters[filterKey] || curFilters[filterKey].indexOf(filterVal) === -1) {
        filters[filterKey] = [filterVal];
        filters.query = replaceFacetValue(curFilters.query, filterKey, filterVal);
      } else {
        filters[filterKey] = [];
        filters.query = removeFacet(curFilters.query, filterKey);
      }
    }

    if (filterKey === 'query') {
      filters.language = extractFacet(filters.query, 'language', 'all');
      filters.type = extractFacet(filters.query, 'type', 'all');

      // When user update the query in the search box or first load at the site.
      // We set the user's cvss2 or cvss3 preference in the state and cookie for future use
      const cvss2Severity =
        extractFacet(filters.query, 'cvss2_severity', null) ||
        extractFacet(filters.query, 'severity', null);
      const cvss3Severity = extractFacet(filters.query, 'cvss3_severity', null);
      if (cvss2Severity || cvss3Severity) {
        const selectedCvssType = cvss3Severity ? 'cvss3' : 'cvss2';
        CvssService.setCvssSelection(selectedCvssType);
        dispatch(updateCvssSelection(selectedCvssType));
      }

      filters.severity = cvss2Severity || cvss3Severity || `0${MODEL.RANGE_SPLITTER}10`;
      filters.disclosed = extractFacet(filters.query, 'disclosed', '');
      filters.released = extractFacet(filters.query, 'released', '');
      filters.cve = extractFacet(filters.query, 'cve', '');
      filters.license = extractFacet(filters.query, 'license', '');
      filters.enhanced = extractFacet(filters.query, 'enhanced', '');
      filters.vulnerable_method = extractFacet(filters.query, 'vulnerable_method', '');
      filters.exploits = extractFacet(filters.query, 'exploits', '');
      filters.vulnerable = extractFacet(filters.query, 'vulnerable', '');
      filters.source = extractFacet(filters.query, 'source', '');
    } else if (filterKey !== 'query' && (filterVal === 'all' || filterVal === 0)) {
      filters.query = removeFacet(filters.query, filterKey);
    }

    if (filterKey === 'type' && filterVal === 'vulnerability') {
      filters.license = '';
      filters.source = [];
      filters.vulnerable = [];
      filters.query = removeFacet(filters.query, 'license');
      filters.query = removeFacet(filters.query, 'source');
      filters.query = removeFacet(filters.query, 'vulnerable'); // library "has vulnerablities"
    }

    if (filterKey === 'type' && filterVal === 'library') {
      filters.severity = `0${MODEL.RANGE_SPLITTER}10`;
      filters.disclosed = '';
      filters.enhanced = [];
      filters.vulnerable_method = [];
      filters.exploits = [];
      filters.query = removeFacet(filters.query, 'cvss2_severity');
      filters.query = removeFacet(filters.query, 'cvss3_severity');
      filters.query = removeFacet(filters.query, 'severity');
      filters.query = removeFacet(filters.query, 'disclosed');
      filters.query = removeFacet(filters.query, 'enhanced');
      filters.query = removeFacet(filters.query, 'vulnerable_method');
      filters.query = removeFacet(filters.query, 'exploits');
    }

    if (['license', 'source'].indexOf(filterKey) >= 0 && curFilters.type !== 'library') {
      filters.type = 'library';
      filters.query = replaceFacetValue(filters.query, 'type', 'library');
    }

    if (
      [
        'disclosed',
        'cvss2_severity',
        'cvss3_severity',
        'severity',
        'vulnerable_method',
        'enhanced',
        'cve',
        'exploits',
      ].indexOf(filterKey) !== -1 &&
      curFilters.type !== 'vulnerability'
    ) {
      filters.type = 'vulnerability';
      filters.license = '';
      filters.source = [];
      filters.query = replaceFacetValue(filters.query, 'type', 'vulnerability');
      filters.query = removeFacet(filters.query, 'license');
      filters.query = removeFacet(filters.query, 'source');
    }

    if (
      filterKey === 'type' &&
      filterVal === 'vulnerability' &&
      (curFilters.license !== '' || curFilters.package_manager !== '')
    ) {
      filters.license = '';
      filters.source = [];
      filters.query = removeFacet(filters.query, 'license');
      filters.query = removeFacet(filters.query, 'source');
    }

    if (filterKey === 'type' && curFilters.type !== 'all' && filterVal === 'all') {
      filters.type = 'all';
      filters.query = removeFacet(filters.query, 'type');
    }

    // Remove source based on selected language
    if (filterKey === 'language') {
      let sourceFacetToRemoveList = [];

      _.each(MODEL.languageToSourceMap, function(source, language) {
        if (!_.includes(filters.language, language)) {
          sourceFacetToRemoveList = sourceFacetToRemoveList.concat(source);
        }
      });

      sourceFacetToRemoveList.forEach(function(sourceFacetToRemove) {
        filters.query = removeFacetValue(filters.query, 'source', sourceFacetToRemove);
      });

      filters.source = _.difference(curFilters.source, sourceFacetToRemoveList);
    }

    const newFilters = Object.assign({}, curFilters, filters);
    dispatch(setNewFilter(newFilters));
    dispatch(search(newFilters.query));
    dispatch(updateIsSearchMode(true));
  };
};

export const updateDateOpt = (key, val) => {
  return {
    type: UPDATE_DATE_OPT,
    key,
    val,
  };
};

export const updateSeverityVal = val => {
  return {
    type: UPDATE_SEVERITY_VAL,
    val,
  };
};

export const updateIsSearchMode = isSearchMode => {
  return {
    type: UPDATE_IS_SEARCH_MODE,
    isSearchMode,
  };
};

export const updateCvssSelection = selection => ({
  type: UPDATE_CVSS_SELECTION,
  cvss: selection,
});
